import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, type CostBreakdown, type DashboardOverview, type MonthlyTrend } from '../lib/api/dashboard';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

// Prototype AI Insights — chưa có LLM, giữ hardcoded
const AI_INSIGHTS = [
  {
    tag: 'Chi phí',
    tone: 'red' as const,
    title: 'CP QLDN vượt trend +32%',
    detail: 'T7 tăng đột biến so trung bình 6 tháng — kiểm tra khoản chi ngoài dự toán.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 17a10 10 0 0 0-20 0" /><path d="M22 17l-4-4-4 4-4-4-4 4" /></svg>
    ),
  },
  {
    tag: 'Doanh thu',
    tone: 'purple' as const,
    title: '3 HĐ đến kỳ thu tuần này',
    detail: 'XYZ Trading, Vintech Corp, Nam Định Group — tổng 345M dự thu.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
    ),
  },
  {
    tag: 'TSCĐ',
    tone: 'yellow' as const,
    title: '4 TSCĐ hết bảo hành Q3',
    detail: 'Máy in Canon, laptop MacBook 2020… — chuẩn bị budget bảo trì.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8M1 3h22v5H1M10 12h4" /></svg>
    ),
  },
];

/** SVG icon cho mỗi loại action trong Activity feed. */
function activityIcon(action: string) {
  const a = action.toLowerCase();
  if (a.includes('duyệt') || a.includes('ký')) {
    return {
      tone: 'green',
      svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>,
    };
  }
  if (a.includes('từ chối') || a.includes('reject')) {
    return {
      tone: 'red',
      svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>,
    };
  }
  if (a.includes('phiếu thu') || a.includes('thu')) {
    return {
      tone: 'yellow',
      svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v18l-4-4-4 4V3" /><path d="M18 3v18" /></svg>,
    };
  }
  if (a.includes('phiếu chi') || a.includes('lập')) {
    return {
      tone: 'blue',
      svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M12 18v-6M9 15h6" /></svg>,
    };
  }
  if (a.includes('bảng lương') || a.includes('payroll')) {
    return {
      tone: 'purple',
      svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6" /></svg>,
    };
  }
  return {
    tone: 'gray',
    svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  };
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const h = Math.floor(diff / 3600000);
  if (h < 1) return `${Math.max(1, Math.floor(diff / 60000))} phút trước`;
  if (h < 24) return `${h} giờ trước`;
  const days = Math.floor(h / 24);
  return `${days} ngày trước`;
}

export function DashboardPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [trend, setTrend] = useState<MonthlyTrend | null>(null);
  const [breakdown, setBreakdown] = useState<CostBreakdown | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    dashboardApi.overview().then(setOverview).catch((e) => setErr((e as Error).message));
    dashboardApi.monthlyTrend(2026).then(setTrend).catch(() => {});
    dashboardApi.costBreakdown(2026).then(setBreakdown).catch(() => {});
  }, []);

  const firstName = (user?.name || '').split(' ').pop() || '';
  // 6 tháng gần nhất tính từ thời điểm hiện tại (bao gồm tháng này) — không lấy future
  const now = new Date();
  const currentMonth = trend && trend.year === now.getFullYear() ? now.getMonth() + 1 : 12;
  const trendMonths = (trend?.months ?? [])
    .filter((m) => m.month <= currentMonth)
    .slice(-6);
  // Bao gồm cả LN để scale bar
  const trendWithProfit = trendMonths.map((m) => ({ ...m, profit: m.revenue - m.cost }));
  const maxBar = trendWithProfit.length
    ? Math.max(1, ...trendWithProfit.flatMap((m) => [m.revenue, m.cost, m.profit]))
    : 1;

  // Trung bình cho footer strip
  const avgDT = trendMonths.length ? trendMonths.reduce((s, m) => s + m.revenue, 0) / trendMonths.length : 0;
  const avgCP = trendMonths.length ? trendMonths.reduce((s, m) => s + m.cost, 0) / trendMonths.length : 0;
  const avgLN = avgDT - avgCP;
  const peakMonth = trendMonths.reduce((peak, m) => m.revenue > peak.revenue ? m : peak, trendMonths[0] ?? { label: '', revenue: 0 });

  // Cumulative pct cho donut
  const arcs = (() => {
    let acc = 0;
    return (breakdown?.items ?? []).map((c) => {
      const start = acc;
      acc += c.pct;
      return { ...c, start };
    });
  })();
  const donutColorMap: Record<string, string> = {
    QLDN: '#2563EB',
    'Nhân sự': '#dc2626',
    'Bán hàng': '#f59e0b',
    Khác: '#7c3aed',
  };

  // Alerts
  const alertApprovals = overview?.alerts.pendingApprovals ?? 0;
  const alertOverdue = overview?.alerts.overduePayments ?? [];
  const alertPayrolls = overview?.alerts.pendingPayrolls ?? 0;

  const overdueDetail = alertOverdue.length > 0
    ? alertOverdue.slice(0, 2).map((o) => `${o.customer} ${o.overdueDays}d · ${formatVND(o.amount)}`).join(' · ')
    : 'Không có HĐ quá hạn';

  return (
    <AppShell title="Bảng điều khiển" subtitle={`Chào ${firstName || user?.name}, hôm nay ${new Date().toLocaleDateString('vi-VN')}`}>
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* Alerts — 3 cards với gradient bg + SVG/count badge */}
      <div className="dsh-alerts">
        <Link to="/approvals" className="dsh-alert dsh-alert-blue">
          <div className="dsh-alert-badge">{alertApprovals}</div>
          <div className="dsh-alert-body">
            <div className="dsh-alert-title">Đề nghị chi chờ tôi duyệt</div>
            <div className="dsh-alert-detail">
              {alertApprovals > 0 ? 'Vào duyệt để không delay batch T6' : 'Sạch queue — tuyệt vời'}
            </div>
          </div>
          <svg className="dsh-alert-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </Link>

        <Link to="/contracts" className="dsh-alert dsh-alert-red">
          <div className="dsh-alert-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
          </div>
          <div className="dsh-alert-body">
            <div className="dsh-alert-title">{alertOverdue.length} kỳ HĐ quá hạn</div>
            <div className="dsh-alert-detail">{overdueDetail}</div>
          </div>
          <svg className="dsh-alert-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </Link>

        <Link to="/payroll" className="dsh-alert dsh-alert-yellow">
          <div className="dsh-alert-badge">{alertPayrolls}</div>
          <div className="dsh-alert-body">
            <div className="dsh-alert-title">Bảng lương chờ xử lý</div>
            <div className="dsh-alert-detail">Chờ KTT duyệt / CEO ký</div>
          </div>
          <svg className="dsh-alert-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </Link>
      </div>

      {/* KPI cards — 3 cards với icon tile + delta pill + gradient border-top */}
      <div className="dsh-kpi-grid">
        <div className="dsh-kpi dsh-kpi-blue">
          <div className="dsh-kpi-head">
            <div className="dsh-kpi-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </div>
            <div className="dsh-kpi-label">Doanh thu YTD 2026</div>
          </div>
          <div className="dsh-kpi-value">{overview ? formatVND(overview.kpi.revenue) : '—'}</div>
          <div className="dsh-kpi-footer">
            <span className="dsh-kpi-pill dsh-kpi-pill-up">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
              Từ TK loại 5
            </span>
          </div>
        </div>

        <div className="dsh-kpi dsh-kpi-green dsh-kpi-highlight">
          <div className="dsh-kpi-head">
            <div className="dsh-kpi-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 6l-9.5 9.5-5-5L1 18" /><polyline points="17 6 23 6 23 12" /></svg>
            </div>
            <div className="dsh-kpi-label">Lợi nhuận sau thuế</div>
          </div>
          <div className="dsh-kpi-value">{overview ? formatVND(overview.kpi.profit) : '—'}</div>
          <div className="dsh-kpi-footer">
            <span className="dsh-kpi-pill dsh-kpi-pill-up">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
              Thuế TNDN 20%
            </span>
          </div>
        </div>

        <div className="dsh-kpi dsh-kpi-purple">
          <div className="dsh-kpi-head">
            <div className="dsh-kpi-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>
            </div>
            <div className="dsh-kpi-label">Chi phí lương YTD</div>
          </div>
          <div className="dsh-kpi-value">{overview ? formatVND(overview.kpi.salary) : '—'}</div>
          <div className="dsh-kpi-footer">
            <span className="dsh-kpi-pill dsh-kpi-pill-neutral">
              TK 3341 (Phải trả NLĐ)
            </span>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="dsh-charts-grid">
        {/* Bar chart 3 series */}
        <div className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Doanh thu vs Chi phí vs LN</h3>
              <p className="dsh-card-sub">6 tháng gần nhất · đơn vị tỷ đồng</p>
            </div>
            <div className="dsh-legend">
              <span><i style={{ background: '#2563EB' }} />DT</span>
              <span><i style={{ background: '#dc2626' }} />CP</span>
              <span><i style={{ background: '#22c55e' }} />LN</span>
            </div>
          </div>
          <div className="dsh-chart-bars">
            {trendWithProfit.length === 0 ? (
              <div className="pay-empty" style={{ width: '100%' }}>Đang tải trend…</div>
            ) : trendWithProfit.map((m) => (
              <div key={m.label} className="dsh-bar-group">
                <div className="dsh-bar-pair">
                  <div className="dsh-bar dsh-bar-dt" style={{ height: `${Math.max(2, (m.revenue / maxBar) * 140)}px` }} title={`DT ${formatVND(m.revenue)}`} />
                  <div className="dsh-bar dsh-bar-cp" style={{ height: `${Math.max(2, (m.cost / maxBar) * 140)}px` }} title={`CP ${formatVND(m.cost)}`} />
                  <div className="dsh-bar dsh-bar-ln" style={{ height: `${Math.max(2, (Math.abs(m.profit) / maxBar) * 140)}px` }} title={`LN ${formatVND(m.profit)}`} />
                </div>
                <div className="dsh-bar-label">{m.label}</div>
              </div>
            ))}
          </div>
          {trendMonths.length > 0 && (
            <div className="dsh-chart-footer">
              <span>Trung bình: <b>DT</b> {formatVND(avgDT)} · <b>CP</b> {formatVND(avgCP)} · <b>LN</b> {formatVND(avgLN)}</span>
              <span>{peakMonth.label} cao nhất kỳ</span>
            </div>
          )}
        </div>

        {/* Donut với center text */}
        <div className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Cơ cấu chi phí 2026</h3>
              <p className="dsh-card-sub">Phân bổ theo loại</p>
            </div>
          </div>
          <div className="dsh-donut-wrap">
            <div className="dsh-donut-container">
              <svg viewBox="0 0 42 42" className="dsh-donut">
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#f1f5f9" strokeWidth="4" />
                {arcs.map((a) => (
                  <circle
                    key={a.label}
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke={donutColorMap[a.label] || a.color}
                    strokeWidth="4"
                    strokeDasharray={`${a.pct} ${100 - a.pct}`}
                    strokeDashoffset={`${100 - a.start + 25}`}
                    transform="rotate(-90 21 21)"
                  />
                ))}
              </svg>
              <div className="dsh-donut-center">
                <span>Tổng</span>
                <b>{breakdown ? formatVND(breakdown.total) : '—'}</b>
              </div>
            </div>
            <ul className="dsh-donut-legend">
              {arcs.map((a) => (
                <li key={a.label}>
                  <i style={{ background: donutColorMap[a.label] || a.color }} />
                  <span>{a.label}</span>
                  <em>{a.pct}%</em>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Quick actions — 4 tiles with SVG in tinted bg */}
      <div className="dsh-card">
        <div className="dsh-card-head"><h3>Thao tác nhanh</h3></div>
        <div className="dsh-quick-grid">
          <Link to="/payments" className="dsh-quick">
            <div className="dsh-quick-icon dsh-quick-icon-blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
            </div>
            <div><b>Lập đề nghị chi</b><p>Yêu cầu thanh toán / Tạm ứng</p></div>
          </Link>
          <Link to="/payroll" className="dsh-quick">
            <div className="dsh-quick-icon dsh-quick-icon-green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6" /></svg>
            </div>
            <div><b>Tạo bảng lương</b><p>Kỳ mới / kỳ đặc biệt</p></div>
          </Link>
          <Link to="/contracts" className="dsh-quick">
            <div className="dsh-quick-icon dsh-quick-icon-orange">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></svg>
            </div>
            <div><b>Thêm hợp đồng</b><p>Khách hàng mới</p></div>
          </Link>
          <Link to="/reports" className="dsh-quick">
            <div className="dsh-quick-icon dsh-quick-icon-purple">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3" /></svg>
            </div>
            <div><b>Xem báo cáo</b><p>P&L · BS · Cash flow</p></div>
          </Link>
        </div>
      </div>

      {/* AI Insights + Activity — 2-col row */}
      <div className="dsh-insight-row">
        {/* AI Insights — gradient shell + stacked white cards */}
        <div className="dsh-ai-shell">
          <div className="dsh-ai-shell-head">
            <div className="dsh-ai-shell-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8 5.8 1.9-5.8 1.9L12 18.4l-1.9-5.8-5.8-1.9 5.8-1.9L12 3z" /><path d="M19 17l.7 2.3L22 20l-2.3.7L19 23l-.7-2.3L16 20l2.3-.7L19 17z" /></svg>
            </div>
            <div>
              <h3>AI Insights</h3>
              <p>3 cảnh báo dự báo trong tuần</p>
            </div>
            <button type="button" className="dsh-ai-shell-tag" onClick={() => { window.location.href = '/reports'; }}>
              Xem tất cả →
            </button>
          </div>
          <div className="dsh-ai-list">
            {AI_INSIGHTS.map((i) => (
              <div key={i.title} className={`dsh-ai-item dsh-ai-item-${i.tone}`}>
                <div className="dsh-ai-item-icon">{i.icon}</div>
                <div className="dsh-ai-item-body">
                  <span className="dsh-ai-tag">{i.tag}</span>
                  <b>{i.title}</b>
                  <p>{i.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activity feed — với avatar tinted */}
        <div className="dsh-card">
          <div className="dsh-card-head">
            <h3>Hoạt động gần đây</h3>
            <button type="button" className="sal-link" onClick={() => { window.location.href = '/approvals'; }}>Xem đầy đủ →</button>
          </div>
          <ul className="dsh-activity">
            {overview?.activity.length ? overview.activity.map((a, i) => {
              const ic = activityIcon(a.action);
              return (
                <li key={i}>
                  <div className={`dsh-act-avatar dsh-act-avatar-${ic.tone}`}>{ic.svg}</div>
                  <div className="dsh-act-meta">
                    <div className="dsh-act-line">
                      <b>{a.actor}</b> {a.action} <b className="dsh-act-code">{a.code}</b>
                    </div>
                    <div className="dsh-act-sub">{a.meta}</div>
                  </div>
                  <span className="dsh-act-time">{relativeTime(a.time)}</span>
                </li>
              );
            }) : (
              <li className="mps-h-empty">{err ? `Lỗi: ${err}` : 'Đang tải hoạt động…'}</li>
            )}
          </ul>
        </div>
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}
